import { keyframes, useMediaQuery } from '@mui/material';

// Shared motion vocabulary for the Contracts wizard.
//
// One source of truth for durations, easings, and keyframes so every surface
// animates with the same feel — instead of the scattered `120ms ease` literals
// that used to live in individual components. Product-tool register: motion
// conveys state (feedback, reveal, view transition), it never choreographs a
// page load. Keep everything in the 120–250ms band.

// Durations (ms).
export const MOTION_DURATION = {
  // Instant feedback: press, hover, color/border shifts.
  feedback: 120,
  // State changes: badge resolve, view transition, alert reveal.
  state: 200,
  // Slightly larger reveals: progress fill, connector sweep, staggered lists.
  emphasis: 240,
} as const;

// Ease-out curves only — no bounce / elastic (they date quickly and pull
// attention to the animation itself rather than the state it conveys).
export const MOTION_EASING = {
  // ease-out-quint — snappy settle, the workhorse.
  standard: 'cubic-bezier(0.22, 1, 0.36, 1)',
  // ease-out-expo — confident, decisive; for the larger reveals.
  decelerate: 'cubic-bezier(0.16, 1, 0.3, 1)',
} as const;

// Per-item delay for staggered list reveals. Cap the staggered count so a long
// list never adds more than ~240ms of cumulative delay.
export const MOTION_STAGGER_MS = 45;
export const MOTION_STAGGER_MAX_ITEMS = 6;

// The signature view transition: new step content fades up into place.
export const stepEnterKeyframes = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// A resolved entity chip landing in place of its loading skeleton.
export const chipResolveKeyframes = keyframes`
  from { opacity: 0; transform: scale(0.85); }
  to   { opacity: 1; transform: scale(1); }
`;

// Async content crossfading in over the skeleton it replaces.
export const fadeInKeyframes = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

// A completion glyph (checkmark) settling in. Pure ease-out, no overshoot.
export const checkPopKeyframes = keyframes`
  from { opacity: 0; transform: scale(0.6); }
  to   { opacity: 1; transform: scale(1); }
`;

// Reduced-motion is not optional. Components branch on this once and fall back
// to an instant state change (no transform, no keyframe). `noSsr` because the
// app is client-rendered — read the real media query up front, no flash.
export const usePrefersReducedMotion = (): boolean =>
  useMediaQuery('(prefers-reduced-motion: reduce)', { noSsr: true });
